import { HttpParams } from '@angular/common/http';
import { IntelligenceModels } from '@models/intelligence.model';

import AdvancedFilter = IntelligenceModels.DeviceHealth.AdvancedFilter;
import DeviceType = IntelligenceModels.DeviceList.DeviceType;
import FacetOption = IntelligenceModels.DeviceHealth.FacetOption;
import Filters = IntelligenceModels.DeviceHealth.Filters;
import FilterOption = IntelligenceModels.DeviceHealth.FilterOption;
import HealthStatus = IntelligenceModels.HealthStatus;

/**
 * Pure filter helpers of the Cmd+K device-health panel: option lists, request
 * params, and the canonical key its response cache is keyed by. Mirrors the MCP
 * widget's `device-health-filters.ts` semantics (`labelOf`, `cacheKey`,
 * `isControlAvailable`) without importing it — that file lives in the backend
 * tree and the frontend must not reach into `@app/models`.
 */

/**
 * Cameras and Cores only — the other seven web device kinds have no pulsation
 * story, so they would render as permanently unknown rows.
 */
export const TYPE_OPTIONS: FilterOption<DeviceType>[] = [
  { value: DeviceType.Camera, labelKey: 'DEVICE_HEALTH.CAMERA' },
  { value: DeviceType.Core, labelKey: 'DEVICE_HEALTH.CORE' },
];

/**
 * Our four OVERALL statuses, not the device-list page's component-status list,
 * and never `Unknown` — device-health does not emit it. Installing is Core-only.
 */
export const STATUS_OPTIONS: FilterOption<HealthStatus>[] = [
  { value: HealthStatus.Online, labelKey: 'DEVICE_HEALTH.ONLINE' },
  { value: HealthStatus.Unhealthy, labelKey: 'DEVICE_HEALTH.UNHEALTHY' },
  { value: HealthStatus.Offline, labelKey: 'DEVICE_HEALTH.OFFLINE' },
  { value: HealthStatus.Installing, labelKey: 'DEVICE_STATUS.INSTALLING' },
];

export const ADVANCED_OPTIONS: FilterOption<AdvancedFilter>[] = Object.values(AdvancedFilter).map(
  (value) => ({ value, labelKey: IntelligenceModels.DeviceHealth.AdvancedFilterLabelKey[value] }),
);

/** Normalizes optional seed data into the filter form's complete shape. */
export function toFilters(source?: Partial<Filters> | null): Filters {
  const name = source?.name?.trim();
  return {
    locations: [...(source?.locations ?? [])],
    types: [...(source?.types ?? [])],
    statuses: [...(source?.statuses ?? [])],
    tagIds: [...(source?.tagIds ?? [])],
    advanced: [...(source?.advanced ?? [])],
    name: name ? name : null,
  };
}

/**
 * Request params of one filter set. Array values are `append`ed, which the DTO
 * reads as repeated `?types=a&types=b`; an empty search is omitted entirely.
 */
export function toHttpParams(filters: Filters): HttpParams {
  let params = new HttpParams();

  for (const location of filters.locations) params = params.append('locations', location);
  for (const type of filters.types) params = params.append('types', type);
  for (const status of filters.statuses) params = params.append('statuses', status);
  for (const tagId of filters.tagIds) params = params.append('tagIds', tagId);
  for (const advanced of filters.advanced) params = params.append('advanced', advanced);

  const name = filters.name?.trim();
  if (name) params = params.set('name', name);

  return params;
}

/**
 * Canonical key of a filter set — order-insensitive across the arrays and
 * case-insensitive on the search, so two spellings of the same intent hit the
 * same cache entry and do not re-request (widget `cacheKey` parity).
 */
export function filtersCacheKey(filters: Filters): string {
  return JSON.stringify([
    [...filters.locations].sort(),
    [...filters.types].sort(),
    [...filters.statuses].sort(),
    [...filters.tagIds].sort(),
    [...filters.advanced].sort(),
    filters.name?.trim().toLowerCase() ?? '',
  ]);
}

/**
 * Wording of a facet value. A prompt-seeded partial location NAME matches no
 * facet id, so it falls back to the raw value — the chip still shows it and the
 * refetch still sends it, while the select shows no selection for it.
 */
export function facetLabel(options: FacetOption[], id: string): string {
  return options.find((option) => option.id === id)?.name ?? id;
}

/** Converts a prompt location to an id only when the served facets match it once. */
export function resolveSeededLocationFilters(filters: Filters, facets: FacetOption[]): Filters {
  return {
    ...filters,
    locations: filters.locations.map((term) => {
      const normalized = term.trim().toLowerCase();
      if (!normalized || facets.some((option) => option.id === term)) return term;

      const exact = facets.filter((option) => option.name.trim().toLowerCase() === normalized);
      const matches = exact.length ? exact : facets.filter((option) => option.name.toLowerCase().includes(normalized));
      return matches.length === 1 ? matches[0]!.id : term;
    }),
  };
}
